import { COMMERCIAL_DOMAINS } from "./commercialProjectSchema";
import { normalizeExpansionWorkOrderDisplay } from "./expansionWorkOrderFormat";

/** Expansion project ACF group keys (match acf_groups_json / registry domainKey). */
export const EXPANSION_ACF_GROUPS = {
  SUBSEGMENT_SUMMARY: "subsegment_summary",
  SUMMARY: "project_summary",
  STATS: "expansion_segment_statistics",
  SUBSEGMENTS: "expansion_subsegments",
};

export const EXPANSION_GROUP_LIST = [
  EXPANSION_ACF_GROUPS.SUBSEGMENT_SUMMARY,
  EXPANSION_ACF_GROUPS.SUMMARY,
  EXPANSION_ACF_GROUPS.STATS,
  EXPANSION_ACF_GROUPS.SUBSEGMENTS,
];

/** Groups accepted by expansion CSV import (includes commercial detail domains). */
export const EXPANSION_IMPORT_GROUP_LIST = [
  ...EXPANSION_GROUP_LIST,
  COMMERCIAL_DOMAINS.ENGINEERING,
  COMMERCIAL_DOMAINS.CONSTRUCTION,
  COMMERCIAL_DOMAINS.FIBER,
  COMMERCIAL_DOMAINS.ACCOUNTING,
];

/** Legacy JSON keys merged when writing summary / stats / subsegments. */
const LEGACY_JSON_KEYS = {
  [EXPANSION_ACF_GROUPS.SUBSEGMENT_SUMMARY]: ["subsegment_summary"],
  [EXPANSION_ACF_GROUPS.SUMMARY]: [
    "project_summary",
    "Project Summary Test 2",
    "project_summary_test_2",
  ],
  [EXPANSION_ACF_GROUPS.STATS]: ["Expansion Segment Statistics", "expansion_segment_statistics"],
  [EXPANSION_ACF_GROUPS.SUBSEGMENTS]: ["Expansion Subsegments"],
};

export const EXPANSION_DEFAULT_DEPARTMENTS = [
  "Engineering",
  "Construction",
  "Fiber",
  "Accounting",
];

const EXPANSION_EXCLUDED_DEPARTMENTS = new Set(["networking", "sales", "finance"]);

/** Expansion projects never involve Sales, Finance, or Networking. */
export function filterExpansionDepartments(deps) {
  if (!Array.isArray(deps)) return [...EXPANSION_DEFAULT_DEPARTMENTS];
  const out = [];
  for (const d of deps) {
    const t = String(d ?? "").trim();
    if (!t) continue;
    if (EXPANSION_EXCLUDED_DEPARTMENTS.has(t.toLowerCase())) continue;
    if (!out.some((x) => x.toLowerCase() === t.toLowerCase())) out.push(t);
  }
  return out.length ? out : [...EXPANSION_DEFAULT_DEPARTMENTS];
}

function isPlainObject(v) {
  return v != null && typeof v === "object" && !Array.isArray(v);
}

function emptyExpansionGroups() {
  const out = {};
  for (const key of EXPANSION_GROUP_LIST) {
    out[key] = {};
  }
  return out;
}

function deepMerge(base, patch) {
  if (!isPlainObject(base)) return isPlainObject(patch) ? { ...patch } : patch;
  if (!isPlainObject(patch)) return base;
  const out = { ...base };
  for (const [k, v] of Object.entries(patch)) {
    if (isPlainObject(v) && isPlainObject(out[k])) {
      out[k] = deepMerge(out[k], v);
    } else {
      out[k] = v;
    }
  }
  return out;
}

function readLegacySlice(j, domainKey) {
  const legacyKeys = LEGACY_JSON_KEYS[domainKey] || [domainKey];
  for (const lk of legacyKeys) {
    if (isPlainObject(j[lk])) return { ...j[lk] };
  }
  if (isPlainObject(j[domainKey])) return { ...j[domainKey] };
  return {};
}

/** Build canonical subsegment_summary from legacy expansion groups. */
export function buildSubsegmentSummaryFromLegacy(groups) {
  const sub = groups[EXPANSION_ACF_GROUPS.SUBSEGMENTS] || {};
  const stats = groups[EXPANSION_ACF_GROUPS.STATS] || {};
  const summary = groups[EXPANSION_ACF_GROUPS.SUMMARY] || {};
  const existing = groups[EXPANSION_ACF_GROUPS.SUBSEGMENT_SUMMARY] || {};
  const subphases =
    existing.subphases ??
    existing.subphases_exp ??
    summary.subphases_exp ??
    summary.subphases ??
    undefined;

  const merged = {
    ...existing,
    work_order: existing.work_order ?? sub.work_order ?? summary.work_order,
    segment: existing.segment ?? sub.segment_number ?? sub.segment,
    project_name: existing.project_name ?? sub.segment_name ?? summary.site_name ?? summary.project,
    status: existing.status ?? summary.status,
    phase: existing.phase ?? summary.phase_exp ?? summary.phase,
    subphase: existing.subphase ?? summary.subphase,
    departments: filterExpansionDepartments(
      existing.departments ?? summary.departments ?? summary.department_involvement
    ),
    bid_out: existing.bid_out ?? sub.bid_out ?? summary.bid_out,
    construction_footage: existing.construction_footage ?? stats.construction_footage,
    override_footage: existing.override_footage ?? stats.override_footage,
    caltrans_footage:
      existing.caltrans_footage ?? stats.caltrans_footage ?? stats.construction_in_ct_row,
    completed_conduit_footage:
      existing.completed_conduit_footage ?? stats.completed_conduit_footage,
    completed_fiber_footage: existing.completed_fiber_footage ?? stats.completed_fiber_footage,
    completed_proofing_footage:
      existing.completed_proofing_footage ?? stats.completed_proofing_footage,
    completed_footage: existing.completed_footage ?? stats.completed_footage,
    handholes: existing.handholes ?? stats.handholes,
    cvin_expansion: existing.cvin_expansion ?? summary.cvin_expansion ?? true,
  };
  if (subphases && typeof subphases === "object") {
    merged.subphases = subphases;
  }
  return merged;
}

/** Sync subsegment_summary fields back to legacy expansion group keys. */
export function syncSubsegmentSummaryToLegacy(groups) {
  const subSummary = groups[EXPANSION_ACF_GROUPS.SUBSEGMENT_SUMMARY];
  if (!isPlainObject(subSummary)) return groups;

  const out = { ...groups };
  const sub = { ...(out[EXPANSION_ACF_GROUPS.SUBSEGMENTS] || {}) };
  const stats = { ...(out[EXPANSION_ACF_GROUPS.STATS] || {}) };
  const summary = { ...(out[EXPANSION_ACF_GROUPS.SUMMARY] || {}) };

  if (subSummary.work_order != null) {
    sub.work_order = subSummary.work_order;
    summary.work_order = subSummary.work_order;
  }
  if (subSummary.segment != null) {
    sub.segment_number = subSummary.segment;
    sub.segment = subSummary.segment;
  }
  if (subSummary.project_name != null) {
    sub.segment_name = subSummary.project_name;
    summary.site_name = subSummary.project_name;
  }
  if (subSummary.status != null) summary.status = subSummary.status;
  if (subSummary.phase != null) {
    summary.phase_exp = subSummary.phase;
    summary.phase = subSummary.phase;
  }
  if (subSummary.subphase != null) summary.subphase = subSummary.subphase;
  if (subSummary.departments != null) {
    summary.departments = subSummary.departments;
    summary.department_involvement = subSummary.departments;
  }
  if (subSummary.bid_out != null) {
    sub.bid_out = subSummary.bid_out;
    summary.bid_out = subSummary.bid_out;
  }
  if (subSummary.construction_footage != null) stats.construction_footage = subSummary.construction_footage;
  if (subSummary.override_footage != null) stats.override_footage = subSummary.override_footage;
  if (subSummary.caltrans_footage != null) {
    stats.caltrans_footage = subSummary.caltrans_footage;
    stats.construction_in_ct_row = subSummary.caltrans_footage;
  }
  if (subSummary.completed_conduit_footage != null) {
    stats.completed_conduit_footage = subSummary.completed_conduit_footage;
  }
  if (subSummary.completed_fiber_footage != null) {
    stats.completed_fiber_footage = subSummary.completed_fiber_footage;
  }
  if (subSummary.completed_proofing_footage != null) {
    stats.completed_proofing_footage = subSummary.completed_proofing_footage;
  }
  if (subSummary.completed_footage != null) {
    stats.completed_footage = subSummary.completed_footage;
  }
  if (subSummary.handholes != null) stats.handholes = subSummary.handholes;
  if (subSummary.subphases != null) {
    summary.subphases_exp = subSummary.subphases;
    summary.subphases = subSummary.subphases;
  }
  summary.cvin_expansion = subSummary.cvin_expansion !== false;

  out[EXPANSION_ACF_GROUPS.SUBSEGMENTS] = sub;
  out[EXPANSION_ACF_GROUPS.STATS] = stats;
  out[EXPANSION_ACF_GROUPS.SUMMARY] = summary;
  return out;
}

function enrichSubsegmentSummary(subSummary, acfGroupsJson) {
  const merged = { ...(isPlainObject(subSummary) ? subSummary : {}) };
  merged.departments = filterExpansionDepartments(merged.departments);

  const j = isPlainObject(acfGroupsJson) ? acfGroupsJson : {};
  const cons =
    j[COMMERCIAL_DOMAINS.CONSTRUCTION] ??
    j.construction_details ??
    j["Construction Details"];
  if (isPlainObject(cons)) {
    if (
      (merged.construction_footage == null || String(merged.construction_footage).trim() === "") &&
      cons.reported_footage != null &&
      String(cons.reported_footage).trim() !== ""
    ) {
      merged.construction_footage = cons.reported_footage;
    }
  }
  return merged;
}

/** Read subsegment_summary from acf_groups_json (canonical + legacy stats merge). */
export function readSubsegmentSummaryFromAcf(acfGroupsJson) {
  const groups = readExpansionGroupsFromAcf(acfGroupsJson);
  return enrichSubsegmentSummary(groups[EXPANSION_ACF_GROUPS.SUBSEGMENT_SUMMARY], acfGroupsJson);
}

export function setNestedGroupValue(groups, groupKey, pathParts, value) {
  if (!groupKey || !Array.isArray(pathParts) || pathParts.length === 0) return groups;
  const out = { ...groups, [groupKey]: { ...(groups[groupKey] || {}) } };
  let cur = out[groupKey];
  for (let i = 0; i < pathParts.length - 1; i += 1) {
    const key = pathParts[i];
    if (!isPlainObject(cur[key])) cur[key] = {};
    cur = cur[key];
  }
  cur[pathParts[pathParts.length - 1]] = value;
  return out;
}

export function mergeExpansionGroupsPartial(baseGroups, patchGroups) {
  const out = emptyExpansionGroups();
  for (const key of EXPANSION_GROUP_LIST) {
    out[key] = deepMerge(baseGroups?.[key], patchGroups?.[key]);
  }
  out[EXPANSION_ACF_GROUPS.SUBSEGMENT_SUMMARY] = buildSubsegmentSummaryFromLegacy(out);
  return syncSubsegmentSummaryToLegacy(out);
}

export function readExpansionGroupsFromAcf(acfGroupsJson) {
  const j = isPlainObject(acfGroupsJson) ? acfGroupsJson : {};
  const out = emptyExpansionGroups();
  for (const domainKey of EXPANSION_GROUP_LIST) {
    out[domainKey] = readLegacySlice(j, domainKey);
  }
  out[EXPANSION_ACF_GROUPS.SUBSEGMENT_SUMMARY] = buildSubsegmentSummaryFromLegacy(out);
  return syncSubsegmentSummaryToLegacy(out);
}

function buildSubphasesJson(subSummary, summary) {
  const subExp =
    (isPlainObject(subSummary?.subphases) && subSummary.subphases) ||
    (isPlainObject(summary?.subphases_exp) && summary.subphases_exp) ||
    null;
  if (!subExp || !Object.keys(subExp).length) return undefined;
  return { subphases_exp: subExp };
}

function slugifyPhaseExp(label) {
  if (label == null || String(label).trim() === "") return null;
  return String(label).trim().toLowerCase().replace(/\s+/g, "_");
}

function workOrderBase(wo) {
  const s = String(wo ?? "").trim();
  if (!s) return null;
  const dot = s.indexOf(".");
  return dot >= 0 ? s.slice(0, dot).trim() || s : s;
}

/** Scalar DB columns synced from expansion import groups. */
export function projectScalarsFromExpansionGroups(groups, flatPayload = {}) {
  const synced = syncSubsegmentSummaryToLegacy(groups);
  const subSummary = synced[EXPANSION_ACF_GROUPS.SUBSEGMENT_SUMMARY] || {};
  const summary = synced[EXPANSION_ACF_GROUPS.SUMMARY] || {};
  const sub = synced[EXPANSION_ACF_GROUPS.SUBSEGMENTS] || {};
  const woRaw =
    subSummary.work_order ?? sub.work_order ?? summary.work_order ?? flatPayload.export_slug ?? "";
  const wo =
    woRaw != null && String(woRaw).trim() !== ""
      ? normalizeExpansionWorkOrderDisplay(String(woRaw).trim())
      : null;
  const name =
    subSummary.project_name ??
    sub.segment_name ??
    summary.site_name ??
    summary.project ??
    summary.title ??
    null;
  const phaseRaw =
    subSummary.phase ?? summary.phase_exp ?? summary.phase ?? flatPayload.phase_label ?? "";
  const phaseExp = phaseRaw != null ? String(phaseRaw).trim() : "";
  const customer = summary.customer_name ?? summary.customer ?? null;
  return {
    work_order: wo,
    work_order_base: wo ? workOrderBase(wo) : null,
    name: name != null && String(name).trim() !== "" ? String(name).trim() : null,
    customer: customer != null && String(customer).trim() !== "" ? String(customer).trim() : null,
    status:
      subSummary.status != null && String(subSummary.status).trim() !== ""
        ? String(subSummary.status).trim()
        : summary.status != null && String(summary.status).trim() !== ""
          ? String(summary.status).trim()
          : null,
    phase_label: phaseExp || null,
    phase_expansion_slug: slugifyPhaseExp(phaseExp),
    subphases_json: buildSubphasesJson(subSummary, summary),
    site_name: summary.site_name != null ? String(summary.site_name).trim() || null : null,
    project_type:
      summary.work_order_type != null ? String(summary.work_order_type).trim() || null : null,
    cvin_expansion: true,
    export_slug:
      flatPayload.export_slug != null && String(flatPayload.export_slug).trim() !== ""
        ? String(flatPayload.export_slug).trim()
        : undefined,
  };
}

/** Write expansion domain slices into acf_groups_json (legacy + snake_case keys). */
export function mergeExpansionIntoAcfGroups(existing, groups) {
  const synced = syncSubsegmentSummaryToLegacy(groups);
  const base = isPlainObject(existing) ? { ...existing } : {};

  for (const domainKey of EXPANSION_GROUP_LIST) {
    const slice = synced[domainKey] && typeof synced[domainKey] === "object" ? synced[domainKey] : {};
    base[domainKey] = { ...slice };
    for (const lk of LEGACY_JSON_KEYS[domainKey] || []) {
      if (lk !== domainKey) base[lk] = { ...slice };
    }
  }

  for (const domainKey of EXPANSION_IMPORT_GROUP_LIST) {
    if (EXPANSION_GROUP_LIST.includes(domainKey)) continue;
    const slice = groups[domainKey];
    if (isPlainObject(slice)) base[domainKey] = { ...slice };
  }

  const summary = synced[EXPANSION_ACF_GROUPS.SUMMARY] || {};
  if (summary.cvin_expansion !== false) {
    for (const lk of LEGACY_JSON_KEYS[EXPANSION_ACF_GROUPS.SUMMARY]) {
      if (isPlainObject(base[lk])) base[lk].cvin_expansion = true;
    }
    if (isPlainObject(base[EXPANSION_ACF_GROUPS.SUMMARY])) {
      base[EXPANSION_ACF_GROUPS.SUMMARY].cvin_expansion = true;
    }
    if (isPlainObject(base[EXPANSION_ACF_GROUPS.SUBSEGMENT_SUMMARY])) {
      base[EXPANSION_ACF_GROUPS.SUBSEGMENT_SUMMARY].cvin_expansion = true;
    }
  }
  return base;
}

export function flatPayloadToExpansionGroups(flatPayload) {
  let groups = emptyExpansionGroups();
  for (const [fieldId, val] of Object.entries(flatPayload)) {
    if (fieldId === "_source" || fieldId === "export_slug") continue;
    const dot = fieldId.indexOf(".");
    if (dot < 0) continue;
    const groupKey = fieldId.slice(0, dot);
    const path = fieldId.slice(dot + 1).split(".").filter(Boolean);
    if (!EXPANSION_IMPORT_GROUP_LIST.includes(groupKey) || !path.length) continue;
    if (!groups[groupKey]) groups[groupKey] = {};
    groups = setNestedGroupValue(groups, groupKey, path, val);
  }
  groups[EXPANSION_ACF_GROUPS.SUMMARY].cvin_expansion = true;
  groups[EXPANSION_ACF_GROUPS.SUBSEGMENT_SUMMARY] = buildSubsegmentSummaryFromLegacy(groups);
  return syncSubsegmentSummaryToLegacy(groups);
}
